import { useFlexFlightStore } from '.'
import { useCruiseStore, useDetailsStore, usePriceStore } from '..'
import { Direction } from '../../../bewotec/openapi'
import { COMFORTAI, flexFlightAirlines, IND, INDAI } from '../../../general/master-data'
import { SelectableRailOptionsFlight, ServiceIncluded } from '../flight/interfaces'
import { DisplayFunctionFlightPrice } from '../price/interfaces'
import { IFetchRailOption, IFetchRailOptionByDirection } from '../rail/interfaces'
import { flightClassName } from './../helper'
import { createFlightDisplay, displayFlightDirection, flexFlightHelper } from './helper.js'
import {
  FlightItem,
  IAirlineItem,
  IExpinetFlexFlightType,
  IFlightDirectionItem,
  IGatewayItem,
  IMappedGateway,
  ITransportationClassItem,
} from './interfaces'

export const gatewayItems = () => {
  const flexFlightStore = useFlexFlightStore()

  const items: IGatewayItem[] = []
  let offset = 0

  items.push({
    key: String(offset++),
    listDisplay: 'Abflughafen wählen',
  })

  const availableGateways: IMappedGateway[] = flexFlightStore.availableGateways

  if (!!availableGateways?.length) {
    for (let i = 0; i < availableGateways.length; ++i) {
      const gateway = availableGateways[i]
      items.push({
        key: String(i + offset),
        listDisplay: `${gateway.locName} (${gateway.locCd})`,
        id: gateway.locCd,
      })
    }
  }

  return items
}

export const flightDirectionItems = (): IFlightDirectionItem[] => {
  const flexFlightStore = useFlexFlightStore()

  const items: IFlightDirectionItem[] = []
  let offset = 0

  items.push({
    key: String(offset++),
    listDisplay: 'Flugrichtung wählen',
  })

  const availableFlightDirections = flexFlightStore.selectedFilters.gateway?.availableFlightDirections

  if (!!availableFlightDirections?.length) {
    availableFlightDirections.sort((a, b) => displayFlightDirection(a).localeCompare(displayFlightDirection(b), 'de'))

    for (let i = 0; i < availableFlightDirections.length; ++i) {
      const direction = availableFlightDirections[i]
      items.push({
        key: String(i + offset),
        listDisplay: displayFlightDirection(direction),
        id: direction,
      })
    }
  }

  return items
}

export const transportationClassItems = (): ITransportationClassItem[] => {
  const flexFlightStore = useFlexFlightStore()

  const items: ITransportationClassItem[] = []
  let offset = 0

  items.push({
    key: String(offset++),
    listDisplay: 'Flugklasse wählen',
  })

  const availableTransportationClasses = flexFlightStore.selectedFilters.gateway?.availableFlightClasses

  if (!!availableTransportationClasses?.length) {
    availableTransportationClasses.sort()
    const sortedTransClasses = availableTransportationClasses.toReversed()

    sortedTransClasses.forEach((sortedTransClass, i) =>
      items.push({
        key: String(i + offset),
        listDisplay: flightClassName(sortedTransClass),
        id: sortedTransClass,
      })
    )
  }

  return items
}

export const airlineItems = () => {
  const items: IAirlineItem[] = []

  if (!!flexFlightAirlines.length) {
    flexFlightAirlines.sort((a, b) => a.value.localeCompare(b.value, 'de'))

    flexFlightAirlines.forEach(airline =>
      items.push({
        display: `${airline.value} (${airline.key})`,
        key: airline.key,
      })
    )
  }

  return items
}

export const flightItems = () => {
  const flexFlightStore = useFlexFlightStore()
  const priceStore = usePriceStore()

  const items: FlightItem[] = []
  let offset = 0

  items.push({
    key: String(offset++),
    listDisplay: 'Wunschflug wählen',
  })

  const availableFlights: IExpinetFlexFlightType[] = flexFlightStore.availableFlights
  const displayFlightPrice: DisplayFunctionFlightPrice = priceStore.flight

  if (!!availableFlights?.length) {
    for (let i = 0; i < availableFlights.length; ++i) {
      const flight = availableFlights[i]
      items.push({
        key: String(i + offset),
        ...createFlightDisplay(flight, displayFlightPrice),
      })
    }
  }

  return items
}

export const gatewayKey = (): string => {
  const flexFlightStore = useFlexFlightStore()

  return flexFlightHelper.findFilterKey(flexFlightStore.selectedFilters.gateway?.locCd, flexFlightStore.gatewayItems)
}

export const flightDirectionKey = (): string => {
  const flexFlightStore = useFlexFlightStore()

  return flexFlightHelper.findFilterKey(
    flexFlightStore.selectedFilters.flightDirection,
    flexFlightStore.flightDirectionItems
  )
}

export const transportationClassKey = (): string => {
  const flexFlightStore = useFlexFlightStore()

  return flexFlightHelper.findFilterKey(
    flexFlightStore.selectedFilters.transportationClass,
    flexFlightStore.transportationClassItems
  )
}

export const flightKey = (): string => {
  const flexFlightStore = useFlexFlightStore()

  return flexFlightHelper.findFilterKey(flexFlightStore.selectedFlight?.id, flexFlightStore.flightItems)
}

export const serviceIncluded = (): ServiceIncluded => {
  const detailsStore = useDetailsStore()
  const cruiseStore = useCruiseStore()
  const flexFlightStore = useFlexFlightStore()

  const priceModel = detailsStore.priceModel!
  const route = cruiseStore.route

  const NBSP = '\u00a0'
  const text = `${NBSP}|${NBSP}inkl. Rail&Fly Ticket`
  const isSelectedRailOption = flexFlightStore.selectedRailOption as IFetchRailOption | null

  const isClassValidForPriceModel: boolean =
    ([IND, INDAI].includes(priceModel) &&
      !!isSelectedRailOption &&
      (flexFlightStore.selectedRailOption as IFetchRailOption).railClassCode === '2') ||
    ([COMFORTAI].includes(priceModel) &&
      !!isSelectedRailOption &&
      (flexFlightStore.selectedRailOption as IFetchRailOption).railClassCode === '1')

  const railDirection = flexFlightStore.railDirection
  const isRailDirectionSameAsOutboundFlightDirection = railDirection === 'both' || railDirection === 'outbound'
  const isRailDirectionSameAsInboundFlightDirection = railDirection === 'both' || railDirection === 'inbound'

  return {
    outbound:
      !route?.germanDeparture && isClassValidForPriceModel && isRailDirectionSameAsOutboundFlightDirection ? text : '',
    inbound:
      !route?.germanArrival && isClassValidForPriceModel && isRailDirectionSameAsInboundFlightDirection ? text : '',
  }
}

export const isNoRailAllowed = (): boolean => {
  const flexFlightStore = useFlexFlightStore()
  const cruiseStore = useCruiseStore()
  const detailsStore = useDetailsStore()

  const cruise = cruiseStore.route
  const isPremiumOrComfortAI = [COMFORTAI, IND, INDAI].includes(detailsStore.priceModel!)
  const hasGermanDeparture = !!cruise?.germanDeparture
  const hasGermanArrival = !!cruise?.germanArrival
  const selectableRailOptions = flexFlightStore.selectableRailOptions
  const isSelectableRailOptions = !!selectableRailOptions && !('error' in selectableRailOptions)
  const isRailAvailableForDirection: boolean =
    (!hasGermanDeparture &&
      isSelectableRailOptions &&
      !!(selectableRailOptions as IFetchRailOptionByDirection)[Direction.O]?.length) ||
    (!hasGermanArrival &&
      isSelectableRailOptions &&
      !!(selectableRailOptions as IFetchRailOptionByDirection)[Direction.R]?.length)

  return !(isPremiumOrComfortAI && isRailAvailableForDirection)
}

export const getRailErrorMessage = () => {
  const flexFlightStore = useFlexFlightStore()
  const cruiseStore = useCruiseStore()

  const railOptions: SelectableRailOptionsFlight = flexFlightStore.selectableRailOptions
  const noAvailabilities =
    !railOptions?.[Direction.RT as keyof SelectableRailOptionsFlight] &&
    !railOptions?.[Direction.R as keyof SelectableRailOptionsFlight] &&
    !railOptions?.[Direction.O as keyof SelectableRailOptionsFlight]
  const route = cruiseStore.route
  const hasGermanDeparture = !!route?.germanDeparture
  const hasGermanArrival = !!route?.germanArrival

  if (hasGermanDeparture && hasGermanArrival) {
    return 'Eine Bahnfahrt ist bei deutschen Start-/Endhäfen nicht möglich'
  }

  if (!!flexFlightStore.selectedFlight && noAvailabilities) {
    return 'Momentan keine Bahnoptionen verfügbar.'
  }

  if (flexFlightStore.railDirection !== 'none' && 'error' in railOptions) {
    return 'Leider ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut...'
  }

  return ''
}

export default {
  gatewayItems,
  flightDirectionItems,
  transportationClassItems,
  airlineItems,
  flightItems,
  gatewayKey,
  flightDirectionKey,
  transportationClassKey,
  flightKey,
  serviceIncluded,
  isNoRailAllowed,
  getRailErrorMessage,
}
